import flatpickr from "flatpickr";
import { DateTime } from "luxon";

const dateTimeSelectors = "[data-podia-datetime]:not([type='hidden'])";
const nextArrow = '<svg width="16" height="16" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 8a1 1 0 001 1h9.586l-3.293 3.293a1 1 0 101.414 1.414l5-5a1 1 0 000-1.414l-5-5a1 1 0 00-1.414 1.414L11.586 7H2a1 1 0 00-1 1z"/></svg>';
const prevArrow = '<svg width="16" height="16" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M15 8a1 1 0 01-1 1H4.414l3.293 3.293a1 1 0 11-1.414 1.414l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L4.414 7H14a1 1 0 011 1z"/></svg>'

const picker = (element) => new DateTimePicker(element)

const attachAll = () => {
  document.querySelectorAll(dateTimeSelectors).forEach((element) => picker(element));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.target.querySelectorAll(dateTimeSelectors).forEach(element => {
        picker(element)
      })
    })
  })

  observer.observe(document.body, { childList: true, subtree: true })
};

class DateTimePicker {
  constructor(element) {
    this.element = element;

    if (!this.timezone) {
      console.error("Podia DateTimePicker", "No timezone was provided! 😢 Bailing out, try again!");
      return;
    }

    this.setIncrementOrDefault(this.element);
    this.setDefaultValue();
    this.rawValue = this.element.value;
    this.initializeComponent();
    this.watchDisabledAttribute();
  }

  get datePickerConfig() {
    return {
      altInput: true,
      altFormat: "M j, Y",
      dateFormat: "Y-m-d",
      monthSelectorType: "static",
      shorthandCurrentMonth: true,
      nextArrow: nextArrow,
      prevArrow: prevArrow,
      minDate: this.minDate,
      onReady: (dates, dateStr, flatpickrInstance) => this.onDatePickerReady(flatpickrInstance),
      onChange: () => this.onChange()
    };
  }

  // Generate an array of minute increments: IE [00, 15, 30, 45]
  get increments() {
    const totalMinOptions = Array.from({length: 60 / this.increment});

    return totalMinOptions.map((_, index) => {
      const minutes = (index * this.increment).toString();
      return minutes.length > 1 ? minutes : `0${minutes}`;
    });
  }

  // Getting data from data attributes
  get timezone() {
    return this.element.getAttribute("data-timezone");
  }

  get skipDefaultValue() {
    return this.element.hasAttribute("data-skip-default-value");
  }

  get timezoneLabel() {
    return DateTime.local().setZone(this.timezone).offsetNameShort;
  }

  get timePickerValue() {
    const time = DateTime.fromFormat(this.timePicker.value, "t");

    if (time.isValid) {
      return this.timePicker.value;
    } else {
      // Returns midnight if no time is selected
      return DateTime.local().startOf('day').toFormat("t");
    }
  }

  get datePickerValue() {
    return this.datePicker.value;
  }

  get minDate() {
    return this.element.getAttribute("data-min-date");
  }

  // Set a default value of 30 when a missing/invalid increment is provided
  setIncrementOrDefault(element) {
    let increment = parseInt(element.getAttribute("data-increment"));

    // No increment was provided. But, that's ok! We probably want to use 30.
    if (!increment) {
      this.increment = 30;
      return;
    }

    // "Boy have you lost your mind because I'll help you find it!" - Stanley Hudson
    if (increment < 1 || increment > 60 || 60 % increment) {
      // We used console.warn! 👊
      console.warn("Podia DateTimePicker", "The data-increment provided is invalid. Falling back to the default value 30.")
      this.increment = 30;
      return;
    }

    this.increment = increment;
  }

  setDefaultValue() {
    // A value was provided, our work here is done!
    if (this.element.value) {
      return;
    }

    // The user doesn't want a default value
    if (this.skipDefaultValue) {
      return;
    }

    const currentTime = DateTime.local().setZone('UTC')

    // Find the next available time from now
    let selectedTime = this.timeOptions.find((option) => {
      const time = DateTime.fromFormat(`${option[0]}:${option[1]} ${option[2]}`, "t").setZone('UTC');

      return currentTime < time;
    })

    let value;

    // Say, it's 11:55pm, that's never less than any of the time options
    if (!selectedTime) {
      value = currentTime.plus({ days: 1 }).startOf('day')
    } else {
      value = DateTime.fromFormat(`${selectedTime[0]}:${selectedTime[1]} ${selectedTime[2]}`, "t").setZone('UTC')
    }

    this.element.value = value.toFormat('y-MM-dd TTT');
  }

  // Functions to handle initialization of components

  initializeComponent() {
    this.element.type = "hidden";

    // Wrap our (now) hidden element in a div
    const wrapperElement = this.createElement('form-row');
    wrapperElement.classList.add('datetime-picker');
    this.wrap(this.element, wrapperElement);

    // Dynamically generate the date/time fields
    this.initializeDatePicker(wrapperElement);
    this.initializeTimePicker(wrapperElement);
  }

  initializeDatePicker(wrapperElement) {
    const datePicker = this.datePicker = this.generateDatePicker();

    wrapperElement.appendChild(datePicker);
    const containerElement = this.createElement('col-md-6');
    containerElement.classList.add('mb-3', 'mb-md-0');

    this.wrap(datePicker, containerElement);

    flatpickr(datePicker, this.datePickerConfig);
  }

  initializeTimePicker(wrapperElement) {
    const timePicker = this.timePicker = this.generateTimePicker();

    wrapperElement.appendChild(timePicker);
    this.wrap(timePicker, this.createElement('col-md-6'));
  }

  // Event Handles for Date and Time components

  onDatePickerReady(flatpickrInstance) {
    flatpickrInstance.setDate(
      DateTime.fromSQL(this.rawValue).setZone(this.timezone).toISO()
    );
  }

  onChange() {
    if (this.datePickerValue) {
      const date = DateTime.fromFormat(`${this.datePickerValue} ${this.timePickerValue}`, "yyyy-MM-dd t");

      this.element.value = date
        .setZone(this.timezone)
        .set({
          year: date.year,
          month: date.month,
          day: date.day,
          hour: date.hour,
          minute: date.minute,
        });

      this.timePicker.value = this.timePickerValue;
      this.timePicker.disabled = false;
    } else {
      this.element.value = null;
      this.timePicker.value = "";
      this.timePicker.disabled = true;
    }

    this.triggerElementChangeEvent();
  }

  // Helper functons used when constructing DOM elements

  get timeOptions() {
    const timeHours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    const periods = ["AM", "PM"];
    let options = []

    periods.forEach((part) => {
      timeHours.forEach((hour) => {
        this.increments.forEach((minute) => {
          options.push([hour, minute, part])
        })
      });
    });

    return options
  }

  generateDatePicker() {
    const datePicker = document.createElement("input");
    datePicker.placeholder = "Choose date"
    datePicker.classList.add("form-control", "flatpickr-input");

    datePicker.disabled = this.element.disabled;

    return datePicker;
  }

  generateTimePicker() {
    const timePicker = document.createElement("select");

    timePicker.innerHTML += '<option value="" disabled selected>Choose time</option>';
    this.timeOptions.forEach(option => {
      timePicker.appendChild(this.selectTimeOption(option[0], option[1], option[2]));
    });

    timePicker.classList.add("custom-select", "time-select");
    timePicker.disabled = !this.datePickerValue;
    timePicker.addEventListener("change", () => this.onChange());

    timePicker.classList.add("form-control");

    timePicker.disabled = this.element.disabled;

    return timePicker;
  }

  createElement(className) {
    const element = document.createElement("div")
    element.classList.add(className)
    return element;
  }

  wrap(el, wrapper) {
    el.parentNode.insertBefore(wrapper, el);
    wrapper.appendChild(el);
  }

  timeIsSelected(hour, minute, period) {
    const date = DateTime.fromSQL(this.rawValue).setZone(this.timezone).toFormat('t')

    return date === `${hour}:${minute} ${period}`
  }

  selectTimeOption(hour, minute, period) {
    const option = document.createElement('option');

    option.value = `${hour}:${minute} ${period}`;
    option.text = `${hour}:${minute} ${period} ${this.timezoneLabel}`;
    option.selected = this.timeIsSelected(hour, minute, period);

    return option;
  }

  triggerElementChangeEvent() {
    const event = new Event("change");

    this.element.dispatchEvent(event);
  }

  // Make sure that if the disabled state of the original input change, update the generated inputs too
  watchDisabledAttribute() {
    const attributesObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.target.parentNode.querySelectorAll("select, input").forEach((element) => {
          // Since we query the parentNode, we will find the same mutated element so we need to ignore it
          if (element != mutation.target) {
            element.disabled = mutation.target.disabled;
          }
        })
      })
    });

    attributesObserver.observe(this.element, { attributes: true, attributeFilter: ["disabled"] });
  }
}

export default { attachAll };
